import React, { useEffect, useMemo, useState } from "react";

const initialForm = {
  name: "",
  slug: "",
  sku: "",
  short_description: "",
  description: "",
  price: 0,
  original_price: 0,
  stock: 0,
  unit: "pcs",
  featured_image: "",
  is_featured: false,
  is_active: true,
  category_id: "",
  rating: 5,
  sold_count: 0,
  condition: "baru",
  weight_gram: 0,
  video_url: "",
};

function makeSlug(text) {
  return String(text || "")
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

function normalizeNumberInput(value, fallback = 0) {
  const raw = String(value ?? "").replace(/,/g, ".").trim();
  if (!raw) return fallback;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : fallback;
}

export default function ProductForm({
  initialData = null,
  categories = [],
  onSubmit,
  onCancel,
  saving = false,
}) {
  const [form, setForm] = useState(initialForm);

  useEffect(() => {
    if (!initialData) {
      setForm(initialForm);
      return;
    }

    setForm({
      ...initialForm,
      ...initialData,
      category_id: initialData?.category_id || initialData?.category?.id || "",
    });
  }, [initialData]);

  const selectedCategory = useMemo(() => {
    return (
      categories.find((item) => String(item.id) === String(form.category_id)) ||
      null
    );
  }, [categories, form.category_id]);

  function updateField(name, value) {
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  function handleNameChange(value) {
    setForm((prev) => {
      const next = { ...prev, name: value };
      if (!prev.slug || prev.slug === makeSlug(prev.name)) {
        next.slug = makeSlug(value);
      }
      return next;
    });
  }

  function handleSubmit(event) {
    event.preventDefault();

    const payload = {
      ...form,
      price: normalizeNumberInput(form.price, 0),
      original_price: normalizeNumberInput(form.original_price, 0),
      stock: Math.max(0, Math.round(normalizeNumberInput(form.stock, 0))),
      rating: normalizeNumberInput(form.rating, 5),
      sold_count: Math.max(0, Math.round(normalizeNumberInput(form.sold_count, 0))),
      weight_gram: Math.max(0, Math.round(normalizeNumberInput(form.weight_gram, 0))),
      category_id: form.category_id ? Number(form.category_id) : null,
      is_featured: Boolean(form.is_featured),
      is_active: Boolean(form.is_active),
    };

    if (onSubmit) onSubmit(payload);
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className="block mb-1 font-medium">Nama Produk</label>
          <input
            className="w-full border rounded-lg px-3 py-2"
            value={form.name}
            onChange={(e) => handleNameChange(e.target.value)}
            required
          />
        </div>

        <div>
          <label className="block mb-1 font-medium">Slug</label>
          <input
            className="w-full border rounded-lg px-3 py-2"
            value={form.slug}
            onChange={(e) => updateField("slug", makeSlug(e.target.value))}
            required
          />
        </div>

        <div>
          <label className="block mb-1 font-medium">SKU</label>
          <input
            className="w-full border rounded-lg px-3 py-2"
            value={form.sku}
            onChange={(e) => updateField("sku", e.target.value)}
          />
        </div>

        <div>
          <label className="block mb-1 font-medium">Kategori</label>
          <select
            className="w-full border rounded-lg px-3 py-2"
            value={form.category_id}
            onChange={(e) => updateField("category_id", e.target.value)}
          >
            <option value="">Pilih kategori</option>
            {categories.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
          {selectedCategory ? (
            <p className="text-xs text-gray-500 mt-1">
              Kategori terpilih: {selectedCategory.name}
            </p>
          ) : null}
        </div>
      </div>

      <div>
        <label className="block mb-1 font-medium">Deskripsi Singkat</label>
        <textarea
          className="w-full border rounded-lg px-3 py-2 min-h-[90px]"
          value={form.short_description}
          onChange={(e) => updateField("short_description", e.target.value)}
        />
      </div>

      <div>
        <label className="block mb-1 font-medium">Deskripsi Lengkap</label>
        <textarea
          className="w-full border rounded-lg px-3 py-2 min-h-[180px]"
          value={form.description}
          onChange={(e) => updateField("description", e.target.value)}
        />
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <div>
          <label className="block mb-1 font-medium">Harga</label>
          <input
            type="number"
            className="w-full border rounded-lg px-3 py-2"
            value={form.price}
            onChange={(e) => updateField("price", e.target.value)}
          />
        </div>

        <div>
          <label className="block mb-1 font-medium">Harga Coret</label>
          <input
            type="number"
            className="w-full border rounded-lg px-3 py-2"
            value={form.original_price}
            onChange={(e) => updateField("original_price", e.target.value)}
          />
        </div>

        <div>
          <label className="block mb-1 font-medium">Stok</label>
          <input
            type="number"
            className="w-full border rounded-lg px-3 py-2"
            value={form.stock}
            onChange={(e) => updateField("stock", e.target.value)}
          />
        </div>

        <div>
          <label className="block mb-1 font-medium">Unit</label>
          <input
            className="w-full border rounded-lg px-3 py-2"
            value={form.unit}
            onChange={(e) => updateField("unit", e.target.value)}
          />
        </div>

        <div>
          <label className="block mb-1 font-medium">Berat (gram)</label>
          <input
            type="number"
            className="w-full border rounded-lg px-3 py-2"
            value={form.weight_gram}
            onChange={(e) => updateField("weight_gram", e.target.value)}
          />
        </div>

        <div>
          <label className="block mb-1 font-medium">Kondisi</label>
          <select
            className="w-full border rounded-lg px-3 py-2"
            value={form.condition}
            onChange={(e) => updateField("condition", e.target.value)}
          >
            <option value="baru">baru</option>
            <option value="bekas">bekas</option>
          </select>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <div>
          <label className="block mb-1 font-medium">Featured Image</label>
          <input
            className="w-full border rounded-lg px-3 py-2"
            value={form.featured_image}
            onChange={(e) => updateField("featured_image", e.target.value)}
          />
        </div>

        <div>
          <label className="block mb-1 font-medium">Video URL</label>
          <input
            className="w-full border rounded-lg px-3 py-2"
            value={form.video_url}
            onChange={(e) => updateField("video_url", e.target.value)}
          />
        </div>

        <div>
          <label className="block mb-1 font-medium">Rating</label>
          <input
            type="number"
            step="0.1"
            className="w-full border rounded-lg px-3 py-2"
            value={form.rating}
            onChange={(e) => updateField("rating", e.target.value)}
          />
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={Boolean(form.is_featured)}
            onChange={(e) => updateField("is_featured", e.target.checked)}
          />
          Featured
        </label>

        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={Boolean(form.is_active)}
            onChange={(e) => updateField("is_active", e.target.checked)}
          />
          Aktif
        </label>
      </div>

      <div className="flex gap-3">
        <button
          type="submit"
          disabled={saving}
          className="px-4 py-2 rounded-lg bg-black text-white"
        >
          {saving ? "Menyimpan..." : initialData?.id ? "Update Produk" : "Tambah Produk"}
        </button>

        {onCancel ? (
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 rounded-lg border"
          >
            Batal
          </button>
        ) : null}
      </div>
    </form>
  );
}